/**
 * MailboxFormDialog — shared Create/Edit dialog for the mailbox registry
 * (design spec `plans/designs/tracks-mailbox-ui-design.md` §4–§5).
 *
 * SECURITY BOUNDARY (binding, spec §8.2): reference-manager UI. The only
 * representation of mail-account authentication material here is
 * `secretsVaultRef` — an opaque vault pointer. This component never reads,
 * renders, or transmits entry values; it only assembles pointers from the
 * keys-only vault data (`VaultEntryRef` from secretsApi, never
 * `readVaultSecret`).
 *
 * Deviations from the spec (dictated by repo reality at implementation time):
 *  - The pointer assembly format `vault:<id>[#<key>]` comes from the email
 *    service consumer contract (`parseVaultRef`, exported by the email receive
 *    module — accepted shapes `vault:vb_123`, `vb_123`, `vault:vb_123#support`),
 *    resolving the spec's §4.1.e open item. The manual-input path passes
 *    values through verbatim.
 *  - No `zod` dependency in admin-ui: validators mirror CreateMailboxSchema /
 *    UpdateMailboxSchema rules by hand (dto/mailbox.dto.ts).
 *  - No `ui/radio-group` primitive: native radios as cards (spec Option 1).
 */
import { useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import {
  ArrowLeftRight,
  Check,
  ChevronDown,
  ChevronRight,
  Copy,
  ExternalLink,
  Inbox,
  Send,
  type LucideIcon,
} from 'lucide-react';

import { useToast } from '@/components/shared/Toast';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useGetVaultQuery, useGetVaultsQuery } from '@/store/api/secretsApi';
import {
  useCreateMailboxMutation,
  useUpdateMailboxMutation,
  type CreateMailboxDto,
  type MailboxMode,
  type MailboxProtocol,
  type MailboxResponse,
  type UpdateMailboxDto,
} from '@/store/api/mailboxesApi';
import { cn } from '@/lib/utils';

const MAX_VAULT_REF_LENGTH = 500;
const MIN_POLL_INTERVAL_MS = 15_000;
const MAX_POLL_INTERVAL_MS = 86_400_000;
const MODES: MailboxMode[] = ['send', 'receive', 'both'];

const MODE_OPTIONS: Array<{ value: MailboxMode; label: string; helper: string; icon: LucideIcon }> =
  [
    { value: 'send', label: 'Send', helper: 'Outgoing mail only', icon: Send },
    { value: 'receive', label: 'Receive', helper: 'Incoming mail only', icon: Inbox },
    { value: 'both', label: 'Both', helper: 'Sends and receives', icon: ArrowLeftRight },
  ];

const VAULT_SENTINEL = '__none__';

/** Mode matrix (spec §4.1.c): receive-capable modes require protocol/config handling. */
function isReceiveCapable(mode: MailboxMode): boolean {
  return mode === 'receive' || mode === 'both';
}

/**
 * Reverse-parse a stored pointer into (vaultId, key). Accepts the shapes the
 * email-service consumer accepts (`vault:<id>`, `<id>`, `vault:<id>#<key>` —
 * mirror of the receive module's parseVaultRef contract).
 * Pure string parsing of the vault REFERENCE — never of any value.
 */
function parseVaultPointer(ref: string): { vaultId: string; key?: string } | undefined {
  const trimmed = ref.trim();
  if (!trimmed) return undefined;
  const [idPart, prefixPart] = trimmed.split('#', 2);
  const vaultId = idPart.replace(/^vault:/, '').trim();
  if (!vaultId || trimmed.length > MAX_VAULT_REF_LENGTH) return undefined;
  return { vaultId, key: prefixPart?.trim() || undefined };
}

function validateAddress(value: string): string | undefined {
  const trimmed = value.trim();
  if (!trimmed || !/^[^\s@]+@[^\s@@]+\.[^\s@]+$/.test(trimmed) || trimmed.length > 320) {
    return 'Enter a valid email address.';
  }
  return undefined;
}

function validateVaultRef(value: string): string | undefined {
  const trimmed = value.trim();
  if (!trimmed) return 'Enter the vault entry reference for this mailbox.';
  if (trimmed.length > MAX_VAULT_REF_LENGTH) return 'Reference must be 500 characters or fewer.';
  return undefined;
}

function validatePollInterval(raw: string): string | undefined {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  if (!/^\d+$/.test(trimmed)) return 'Must be a whole number.';
  const value = Number(trimmed);
  if (value < MIN_POLL_INTERVAL_MS) return 'Minimum is 15,000 ms.';
  if (value > MAX_POLL_INTERVAL_MS) return 'Maximum is 86,400,000 ms.';
  return undefined;
}

/** Field keys for the submit error summary (spec §4.3). */
type FieldKey = 'address' | 'protocol' | 'secretsVaultRef' | 'pollIntervalMs';

const FIELD_LABELS: Record<FieldKey, string> = {
  address: 'Mailbox address',
  protocol: 'Protocol',
  secretsVaultRef: 'Vault reference',
  pollIntervalMs: 'Poll interval (ms)',
};

export interface MailboxFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  /** Row under edit (Edit opens exclusively for active rows — spec §5.1). */
  mailbox?: MailboxResponse;
}

export function MailboxFormDialog({ open, onOpenChange, mode, mailbox }: MailboxFormDialogProps) {
  const isEdit = mode === 'edit';
  const toast = useToast();
  const [createMailbox, { isLoading: isCreating }] = useCreateMailboxMutation();
  const [updateMailbox, { isLoading: isSaving }] = useUpdateMailboxMutation();
  const isPending = isCreating || isSaving;

  const formRef = useRef<HTMLFormElement>(null);
  const addressRef = useRef<HTMLInputElement>(null);
  const protocolRef = useRef<HTMLDivElement>(null);
  const vaultRef = useRef<HTMLDivElement>(null);
  const pollRef = useRef<HTMLInputElement>(null);

  const [address, setAddress] = useState('');
  const [selectedMode, setSelectedMode] = useState<MailboxMode>('send');
  const [protocol, setProtocol] = useState<MailboxProtocol | ''>('');
  const [vaultPath, setVaultPath] = useState<'vault' | 'manual'>('vault');
  const [vaultId, setVaultId] = useState('');
  const [entryKey, setEntryKey] = useState('');
  const [manualRef, setManualRef] = useState('');
  const [assembledRef, setAssembledRef] = useState('');
  const [vaultTouched, setVaultTouched] = useState(false);
  const [autoCreateTickets, setAutoCreateTickets] = useState(false);
  const [pollRaw, setPollRaw] = useState('');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<FieldKey, string>>>({});
  const [blurred, setBlurred] = useState<Partial<Record<FieldKey, boolean>>>({});
  const [summary, setSummary] = useState<string[]>([]);

  const {
    data: vaults,
    isError: vaultsError,
    isSuccess: vaultsLoaded,
  } = useGetVaultsQuery(undefined, { skip: !open });

  // Prefill / reset each time the dialog opens (create = defaults, edit = row).
  useEffect(() => {
    if (!open) return;
    const initialMode: MailboxMode = mailbox?.mode ?? 'send';
    setAddress(mailbox?.address ?? '');
    setSelectedMode(initialMode);
    setProtocol(mailbox?.protocol ?? '');
    const storedRef = mailbox?.secretsVaultRef ?? '';
    const parsedPointer = mailbox ? parseVaultPointer(storedRef) : undefined;
    if (parsedPointer) {
      // Preselect the picker from the stored pointer (keys-only parsing; if the
      // vault is not in the accessible list the selects show placeholders, and
      // an untouched picker keeps sending the stored value verbatim).
      setVaultPath('vault');
      setVaultId(parsedPointer.vaultId);
      setEntryKey(parsedPointer.key ?? '');
      setManualRef('');
    } else {
      // Unparseable / empty (create defaults, or a foreign-format pointer):
      // manual path, verbatim fallback.
      setVaultPath(mailbox && storedRef ? 'manual' : 'vault');
      setVaultId('');
      setEntryKey('');
      setManualRef(storedRef);
    }
    setAssembledRef('');
    setVaultTouched(false);
    setAutoCreateTickets(mailbox?.config?.autoCreateTickets ?? false);
    setPollRaw(
      mailbox?.config?.pollIntervalMs != null ? String(mailbox.config.pollIntervalMs) : '',
    );
    setSettingsOpen(Boolean(mailbox?.config));
    setErrors({});
    setBlurred({});
    setSummary([]);
    setCopied(false);
  }, [open, mailbox]);

  const selectedVault = useMemo(
    () => (vaults ?? []).find((vault) => vault._id === vaultId),
    [vaults, vaultId],
  );

  // `GET /secrets/vaults` projects out `entries` (backend `.select('-entries')`),
  // so entry keys only exist on the vault DETAIL endpoint. Fetch it once a vault
  // is picked; the list stays the source for the picker labels.
  const { data: selectedVaultDetail } = useGetVaultQuery(vaultId, {
    skip: !open || vaultPath !== 'vault' || !vaultId,
  });

  const vaultEntries = useMemo(
    () => selectedVaultDetail?.entries ?? selectedVault?.entries ?? [],
    [selectedVaultDetail, selectedVault],
  );

  // Assemble the pointer whenever vault/entry selection changes (contract:
  // parseVaultRef accepts `vault:<id>` and `vault:<id>#<prefix>`).
  useEffect(() => {
    if (vaultPath !== 'vault') return;
    if (!selectedVault) {
      setAssembledRef('');
      return;
    }
    setAssembledRef(
      entryKey ? `vault:${selectedVault._id}#${entryKey}` : `vault:${selectedVault._id}`,
    );
  }, [vaultPath, selectedVault, entryKey]);

  // Untouched picker in edit mode falls back to the stored pointer (PATCH
  // semantics: unchanged keys stay out of the body — spec §5.1).
  const secretsVaultRef =
    vaultPath === 'manual'
      ? manualRef.trim()
      : vaultTouched
        ? assembledRef
        : (mailbox?.secretsVaultRef ?? assembledRef);

  // Mode matrix: hidden fields are cleared from state so they are never sent
  // (the `.strict()` refine rules reject send + receive-config).
  function handleModeChange(next: MailboxMode) {
    setSelectedMode(next);
    if (!isReceiveCapable(next)) {
      setProtocol('');
      setAutoCreateTickets(false);
      setPollRaw('');
      setSettingsOpen(false);
      setErrors((current) => ({ ...current, protocol: undefined, pollIntervalMs: undefined }));
    }
  }

  // ---- Edit dirty tracking (spec §5.1): diff of changed keys only ----
  function patchPayload(): UpdateMailboxDto {
    const body: UpdateMailboxDto = {};
    const trimmedAddress = address.trim();
    if (trimmedAddress !== (mailbox?.address ?? '')) body.address = trimmedAddress;
    if (selectedMode !== (mailbox?.mode ?? 'send')) body.mode = selectedMode;
    if (isReceiveCapable(selectedMode)) {
      if (protocol && protocol !== (mailbox?.protocol ?? '')) body.protocol = protocol;
      const poll = pollValue();
      const configChanged =
        autoCreateTickets !== (mailbox?.config?.autoCreateTickets ?? false) ||
        poll !== (mailbox?.config?.pollIntervalMs ?? undefined);
      // Replace semantics service-side (mailboxes.service update path): the
      // sent `config` object fully overwrites the companion row — omitted
      // keys clear to defaults.
      if (configChanged) {
        body.config = {
          ...(autoCreateTickets ? { autoCreateTickets: true } : {}),
          ...(poll !== undefined ? { pollIntervalMs: poll } : {}),
        };
      }
    }
    if (secretsVaultRef && secretsVaultRef !== (mailbox?.secretsVaultRef ?? '')) {
      body.secretsVaultRef = secretsVaultRef;
    }
    return body;
  }

  const patch = useMemo(
    () => (isEdit && open ? patchPayload() : {}),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- recompute on every form field
    [isEdit, open, address, selectedMode, protocol, autoCreateTickets, pollRaw, secretsVaultRef],
  );
  const isDirty = isEdit && Object.keys(patch).length > 0;

  // ---- Validation ----
  function validateAll(): Partial<Record<FieldKey, string>> {
    const next: Partial<Record<FieldKey, string>> = {};
    const addressError = validateAddress(address);
    if (addressError) next.address = addressError;
    // Edit rows with a stored protocol arrive prefilled; a missing selection
    // here means create-mode or an edit send→receive transition (spec §5.1).
    if (isReceiveCapable(selectedMode) && !protocol) {
      next.protocol = isEdit ? 'Choose a protocol for incoming mail.' : 'Choose IMAP or POP3.';
    }
    if (!secretsVaultRef) {
      next.secretsVaultRef = 'Enter the vault entry reference for this mailbox.';
    } else {
      const refError = validateVaultRef(secretsVaultRef);
      if (refError) next.secretsVaultRef = refError;
    }
    const pollError = validatePollInterval(pollRaw);
    if (pollError) next.pollIntervalMs = pollError;
    return next;
  }

  /** Current valid poll-interval value, or undefined (blank / invalid). */
  function pollValue(): number | undefined {
    if (validatePollInterval(pollRaw)) return undefined;
    const trimmed = pollRaw.trim();
    return trimmed ? Number(trimmed) : undefined;
  }

  function scrollToField(key: FieldKey) {
    const map: Record<FieldKey, RefObject<HTMLElement | null>> = {
      address: addressRef,
      protocol: protocolRef,
      secretsVaultRef: vaultRef,
      pollIntervalMs: pollRef,
    };
    map[key].current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (key === 'address') addressRef.current?.focus();
    if (key === 'pollIntervalMs') pollRef.current?.focus();
  }

  function buildCreatePayload(): CreateMailboxDto {
    const body: CreateMailboxDto = {
      address: address.trim(),
      mode: selectedMode,
      secretsVaultRef,
    };
    if (isReceiveCapable(selectedMode)) {
      body.protocol = protocol as MailboxProtocol;
      const poll = pollValue();
      if (autoCreateTickets || poll !== undefined) {
        body.config = {
          ...(autoCreateTickets ? { autoCreateTickets: true } : {}),
          ...(poll !== undefined ? { pollIntervalMs: poll } : {}),
        };
      }
    }
    return body;
  }

  async function submit() {
    setBlurred({ address: true, protocol: true, secretsVaultRef: true, pollIntervalMs: true });
    const found = validateAll();
    setErrors(found);
    const failed = Object.keys(found) as FieldKey[];
    if (failed.length > 0) {
      setSummary(failed.map((key) => FIELD_LABELS[key]));
      scrollToField(failed[0]);
      return;
    }
    setSummary([]);
    try {
      if (isEdit) {
        if (!mailbox || !isDirty) return;
        await updateMailbox({ id: mailbox.id, body: patch }).unwrap();
        toast.success('Changes saved');
      } else {
        await createMailbox(buildCreatePayload()).unwrap();
        toast.success('Mailbox created');
      }
      onOpenChange(false);
    } catch (err) {
      const status = (err as { status?: number }).status;
      const message =
        (err as { data?: { message?: string } }).data?.message ??
        'Could not save the mailbox. Try again.';
      if (status === 409) {
        // Address collision also covers reserved/disabled rows (controller:125–129).
        setErrors((current) => ({
          ...current,
          address: 'That address is already registered or reserved.',
        }));
        setSummary(['Mailbox address']);
        scrollToField('address');
      } else if (status === 400) {
        // Server-side rule divergence → summary; inputs are never cleared (spec §4.3/E1).
        setSummary([message]);
      } else {
        toast.error(message);
      }
    }
  }

  const showProtocol = isReceiveCapable(selectedMode);
  const vaultsUnavailable = vaultsLoaded && (vaults?.length ?? 0) === 0;
  const noVaultGuidance = vaultsError || vaultsUnavailable;
  const busy = isPending;

  const fieldError = (key: FieldKey) =>
    blurred[key] || summary.length > 0 ? errors[key] : undefined;

  return (
    <Dialog open={open} onOpenChange={(next) => !busy && onOpenChange(next)}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit mailbox' : 'Create mailbox'}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Update this mailbox. Only changed fields are sent.'
              : 'Register a mailbox address and point it at its vault entry.'}
          </DialogDescription>
        </DialogHeader>
        <form
          ref={formRef}
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          {summary.length > 0 && (
            <div
              role="alert"
              className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm"
            >
              <p className="mb-1 font-medium">Please fix the following:</p>
              <ul className="list-disc space-y-0.5 pl-5">
                {summary.map((label) => (
                  <li key={label}>
                    <button
                      type="button"
                      className="text-destructive underline"
                      onClick={() => {
                        const key = (Object.keys(FIELD_LABELS) as FieldKey[]).find(
                          (k) => FIELD_LABELS[k] === label,
                        );
                        if (key) scrollToField(key);
                      }}
                    >
                      {label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <p className="text-xs text-muted-foreground">* Required</p>

          {/* a. Mailbox address */}
          <div className="space-y-2">
            <Label htmlFor="mailbox-address">* Mailbox address</Label>
            <Input
              id="mailbox-address"
              ref={addressRef}
              type="email"
              inputMode="email"
              autoComplete="off"
              placeholder="e.g. support@yourcompany.com"
              disabled={busy}
              aria-invalid={Boolean(fieldError('address'))}
              aria-describedby={fieldError('address') ? 'mailbox-address-error' : undefined}
              value={address}
              onChange={(event) => setAddress(event.target.value)}
              onBlur={() => {
                setBlurred((current) => ({ ...current, address: true }));
                setErrors((current) => ({ ...current, address: validateAddress(address) }));
              }}
            />
            {fieldError('address') && (
              <p id="mailbox-address-error" className="text-sm text-destructive">
                {fieldError('address')}
              </p>
            )}
          </div>

          {/* b. Access mode — native radio cards (no ui/radio-group exists) */}
          <fieldset disabled={busy}>
            <legend className="mb-2 text-sm font-medium">* Access mode</legend>
            <div className="grid gap-2 sm:grid-cols-3">
              {MODE_OPTIONS.map(({ value, label, helper, icon: Icon }) => (
                <label
                  key={value}
                  className={cn(
                    'flex min-h-11 cursor-pointer flex-col justify-center gap-1 rounded-md border p-3 text-sm transition-colors',
                    'has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring has-[:focus-visible]:ring-offset-2',
                    selectedMode === value
                      ? 'border-primary ring-2 ring-primary/20'
                      : 'hover:bg-muted/30',
                  )}
                >
                  <input
                    type="radio"
                    name="mailbox-mode"
                    className="peer sr-only"
                    checked={selectedMode === value}
                    onChange={() => handleModeChange(value)}
                    aria-describedby={`mode-helper-${value}`}
                  />
                  <span className="flex items-center gap-2 font-medium">
                    <Icon className="h-4 w-4" aria-hidden="true" />
                    {label}
                  </span>
                  <span id={`mode-helper-${value}`} className="text-xs text-muted-foreground">
                    {helper}
                  </span>
                </label>
              ))}
            </div>
          </fieldset>

          {/* d. Protocol — rendered ONLY when receive-capable (cleared otherwise) */}
          {showProtocol && (
            <div ref={protocolRef} className="space-y-2">
              <Label htmlFor="mailbox-protocol">* Protocol</Label>
              <Select
                value={protocol || ''}
                onValueChange={(value) => {
                  setProtocol(value as MailboxProtocol);
                  setErrors((current) => ({ ...current, protocol: undefined }));
                }}
                disabled={busy}
              >
                <SelectTrigger
                  id="mailbox-protocol"
                  className="w-40"
                  aria-invalid={Boolean(fieldError('protocol'))}
                >
                  <SelectValue placeholder="Select a protocol" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="imap">IMAP</SelectItem>
                  <SelectItem value="pop3">POP3</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Required for incoming protocols. IMAP recommended for two-way mail.
              </p>
              {fieldError('protocol') && (
                <p className="text-sm text-destructive">{fieldError('protocol')}</p>
              )}
            </div>
          )}

          <Separator />

          {/* e. Vault reference — pointer picker. NEVER touches entry values. */}
          <div ref={vaultRef} className="space-y-3">
            <div className="space-y-1">
              <Label>* Vault reference</Label>
              <p className="text-xs text-muted-foreground">
                Connection details stay in the vault. This field only points to them.
              </p>
            </div>

            <fieldset className="flex flex-wrap gap-2" disabled={busy}>
              <legend className="sr-only">Vault reference input path</legend>
              {(
                [
                  { value: 'vault', label: 'Choose from vault' },
                  { value: 'manual', label: 'Enter reference manually' },
                ] as const
              ).map(({ value, label }) => (
                <label
                  key={value}
                  className={cn(
                    'flex min-h-10 cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm',
                    'has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring has-[:focus-visible]:ring-offset-2',
                    vaultPath === value
                      ? 'border-primary ring-2 ring-primary/20'
                      : 'hover:bg-muted/30',
                  )}
                >
                  <input
                    type="radio"
                    name="vault-ref-path"
                    className="sr-only"
                    checked={vaultPath === value}
                    onChange={() => {
                      setVaultPath(value);
                      setVaultTouched(true);
                    }}
                  />
                  {label}
                </label>
              ))}
            </fieldset>

            {noVaultGuidance ? (
              <div className="space-y-2 rounded-md border border-dashed p-3">
                <p className="text-sm text-muted-foreground">
                  No vaults you can access yet. Create one first, then come back.
                </p>
                <Button variant="link" asChild className="h-auto p-0 text-sm">
                  <a href="/secrets" target="_blank" rel="noopener">
                    Open secrets vault page
                    <ExternalLink className="ml-1 h-3.5 w-3.5" />
                  </a>
                </Button>
              </div>
            ) : vaultPath === 'vault' ? (
              <div className="space-y-3">
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1">
                    <Label htmlFor="vault-picker" className="text-xs text-muted-foreground">
                      Vault
                    </Label>
                    <Select
                      value={vaultId || ''}
                      onValueChange={(value) => {
                        setVaultId(value);
                        setEntryKey('');
                        setVaultTouched(true);
                      }}
                      disabled={busy}
                    >
                      <SelectTrigger id="vault-picker">
                        <SelectValue placeholder="Select a vault" />
                      </SelectTrigger>
                      <SelectContent>
                        {(vaults ?? []).map((vault) => (
                          <SelectItem key={vault._id} value={vault._id}>
                            {vault.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="entry-picker" className="text-xs text-muted-foreground">
                      Entry key (optional)
                    </Label>
                    <Select
                      value={entryKey || VAULT_SENTINEL}
                      onValueChange={(value) => setEntryKey(value === VAULT_SENTINEL ? '' : value)}
                      disabled={busy || !selectedVault || vaultEntries.length === 0}
                    >
                      <SelectTrigger id="entry-picker">
                        <SelectValue
                          placeholder={
                            selectedVault ? 'All entries in this vault' : 'Select a vault first'
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={VAULT_SENTINEL}>All entries in this vault</SelectItem>
                        {(vaultEntries ?? []).map((entry) => (
                          <SelectItem key={entry.key} value={entry.key}>
                            “{entry.key}” · updated {new Date(entry.updatedAt).toLocaleDateString()}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {secretsVaultRef && (
                  <div className="flex items-center justify-between gap-2">
                    <code className="break-all rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
                      {secretsVaultRef}
                    </code>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-10 w-10 shrink-0"
                            aria-label="Copy vault reference"
                            onClick={() => {
                              void navigator.clipboard?.writeText(secretsVaultRef);
                              setCopied(true);
                              window.setTimeout(() => setCopied(false), 1_500);
                            }}
                          >
                            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>{copied ? 'Copied' : 'Copy'}</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                <Input
                  placeholder="e.g. the reference shown on the entry in the vault"
                  disabled={busy}
                  aria-invalid={Boolean(fieldError('secretsVaultRef'))}
                  value={manualRef}
                  onChange={(event) => setManualRef(event.target.value)}
                  onBlur={() => {
                    setBlurred((current) => ({ ...current, secretsVaultRef: true }));
                    setErrors((current) => ({
                      ...current,
                      secretsVaultRef: validateVaultRef(manualRef),
                    }));
                  }}
                />
                <p className="text-xs text-muted-foreground">
                  Must match a vault entry reference exactly.
                </p>
              </div>
            )}
            {fieldError('secretsVaultRef') && (
              <p className="text-sm text-destructive">{fieldError('secretsVaultRef')}</p>
            )}
          </div>

          {/* f. Receive settings — progressive disclosure, receive-capable only */}
          {showProtocol && (
            <div className="space-y-3 rounded-md border p-3">
              <button
                type="button"
                className="flex min-h-10 w-full items-center justify-between text-sm font-medium"
                aria-expanded={settingsOpen}
                onClick={() => setSettingsOpen((current) => !current)}
              >
                Receive settings (optional)
                {settingsOpen ? (
                  <ChevronDown className="h-4 w-4" aria-hidden="true" />
                ) : (
                  <ChevronRight className="h-4 w-4" aria-hidden="true" />
                )}
              </button>
              {settingsOpen && (
                <div className="space-y-4">
                  <label className="flex min-h-10 cursor-pointer items-center gap-2 text-sm">
                    <Checkbox
                      checked={autoCreateTickets}
                      disabled={busy}
                      onCheckedChange={(checked) => setAutoCreateTickets(checked === true)}
                    />
                    Auto-create tickets
                  </label>
                  <div className="space-y-2">
                    <Label htmlFor="poll-interval">Poll interval (ms)</Label>
                    <Input
                      id="poll-interval"
                      ref={pollRef}
                      type="number"
                      step={1000}
                      inputMode="numeric"
                      className="w-48"
                      disabled={busy}
                      aria-invalid={Boolean(fieldError('pollIntervalMs'))}
                      value={pollRaw}
                      onChange={(event) => setPollRaw(event.target.value)}
                      onBlur={() => {
                        setBlurred((current) => ({ ...current, pollIntervalMs: true }));
                        setErrors((current) => ({
                          ...current,
                          pollIntervalMs: validatePollInterval(pollRaw),
                        }));
                      }}
                    />
                    <p className="text-xs text-muted-foreground">
                      Integer, 15,000–86,400,000 (15 seconds to 24 hours). Blank = service default.
                    </p>
                    {fieldError('pollIntervalMs') && (
                      <p className="text-sm text-destructive">{fieldError('pollIntervalMs')}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={busy}
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            {isEdit ? (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button type="submit" disabled={!isDirty || busy}>
                      {isSaving ? 'Saving…' : 'Save changes'}
                    </Button>
                  </TooltipTrigger>
                  {!isDirty && <TooltipContent>No changes yet</TooltipContent>}
                </Tooltip>
              </TooltipProvider>
            ) : (
              <Button type="submit" disabled={busy}>
                {isCreating ? 'Creating…' : 'Create mailbox'}
              </Button>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
